/**
 * Media helpers — normalise a raw URL string into a `MediaItem` for the
 * hero carousel and Photo Explorer. Supports:
 *   - Uploaded MP4/WebM (served from /api/uploads/...)
 *   - YouTube  (watch?v=, youtu.be/, embed/)
 *   - Vimeo    (player.vimeo.com/video/ or vimeo.com/<id>)
 *   - Direct image URLs (jpg/png/webp)
 */

export function toMediaItem(raw) {
  if (!raw || typeof raw !== "string") return null;
  const url = raw.trim();

  // YouTube
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) {
    const id = yt[1];
    return {
      type: "video",
      provider: "youtube",
      src: url,
      embed: `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`,
      thumb: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
    };
  }

  // Vimeo
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) {
    const id = vm[1];
    return {
      type: "video",
      provider: "vimeo",
      src: url,
      embed: `https://player.vimeo.com/video/${id}?byline=0&portrait=0`,
      thumb: null, // Vimeo thumbs need API call — fall back to generic
    };
  }

  // MP4 / WebM / MOV
  if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(url)) {
    return { type: "video", provider: "file", src: url, embed: null, thumb: null };
  }

  // Default: treat as image
  return { type: "image", provider: null, src: url, embed: null, thumb: url };
}

/** Build a single media list for a property — videos first, then all photos. */
export function buildMediaList(property) {
  const videos = (property?.videos || [])
    .map(toMediaItem)
    .filter((m) => m && m.type === "video");
  const photos = [property?.hero_image, ...(property?.gallery || [])]
    .filter(Boolean)
    .map(toMediaItem)
    .filter((m) => m && m.type === "image");
  return [...videos, ...photos];
}
